import React, { PureComponent, useState, useEffect, Suspense, useContext, useCallback, useMemo } from 'react';
import { Routes, Route, useNavigate } from 'react-router';
import 'firebaseui/dist/firebaseui.css';
import { auth, deviceSignOut } from './services/firebase';
import { Grid, Paper, CircularProgress, Backdrop } from '@mui/material';
import ReportProblemTwoToneIcon from '@mui/icons-material/ReportProblemTwoTone';
import Layout from './components/Layout';
import './App.less';
import './style.less';
import { AppContext } from './context/provider';
import { removeBrowserContextProperty, setBrowserContextProperty } from './lib/datadog';
import { loadPermissions } from './services/validateUserLogin';
import { access, shortAccess, shortFeatureNames } from './util/FeatureTypes';
import getFeatureFlag from './util/getFeatureFlag';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const queryClient = new QueryClient();

const RateCalendar = React.lazy(() => import('./components/RateSchedule/RateCalendar'));
const AddLocationCard = React.lazy(() => import('./components/AddLocation/AddLocationCard'));
const ListLocations = React.lazy(() => import('./components/ManageLocations/ListLocations'));
const EditLocation = React.lazy(() => import('./components/ManageLocations/EditLocation'));
const LocationAdded = React.lazy(() => import('./components/AddLocation/LocationAdded'));
const EnforcementLite = React.lazy(() => import('./components/EnforcementLite'));
const AddEnforcementLiteLocation = React.lazy(() => import('./components/EnforcementLite/AddEnforcementLiteLocation'));
const EditEnforcementLiteLocation = React.lazy(() => import('./components/EnforcementLite/EditEnforcementLiteLocation'));
const EditEnforcementLiteUser = React.lazy(() => import('./components/EnforcementLite/EditEnforcementLiteUser'));
const AddEnforcementLiteUser = React.lazy(() => import('./components/EnforcementLite/AddEnforcementLiteUser'));

const Login = React.lazy(() => import('./components/Auth/Login'));
const ListV2 = React.lazy(() => import('./components/Enforcement'));
const RateChanges = React.lazy(() => import('./components/RateChanges'));
const PromoCodeForm = React.lazy(() => import('./components/PromoCodes/PromoCodeForm'));
const PromoCodes = React.lazy(() => import('./components/PromoCodes'));
const ParkingPasses = React.lazy(() => import('./components/ParkingPasses'));
const RateConfig = React.lazy(() => import('./components/RateConfig'));
const RoleConfig = React.lazy(() => import('./components/RoleConfig'));
const UserConfig = React.lazy(() => import('./components/UserConfig'));
const NoPermissionsDashboard = React.lazy(() => import('./components/Auth/NoPermissionsDashboard'));
const Dashboard = React.lazy(() => import('./components/Dashboard'));
const AutoRedirect = React.lazy(() => import('./components/Layout/AutoRedirect'));
const Mailing = React.lazy(() => import('./components/Mailing'));
const DatabaseCopy = React.lazy(() => import('./components/DatabaseCopy'));
const Transactions = React.lazy(() => import('./components/Transactions'));
const TenantManagement = React.lazy(() => import('./components/TenantManagement'));
const ManageTenant = React.lazy(() => import('./components/TenantManagement/ManageTenant'));
const TenantValidations = React.lazy(() => import('./components/TenantValidations'));
const ConnectAccounts = React.lazy(() => import('./components/ConnectAccounts'));

import ROLE_IDS from './util/RoleIds';

function App() {
  const [userEmail, setUserEmail] = useState(null);
  const [authEnabled, setAuthEnabled] = useState(false);
  const [firebaseDone, setFirebaseDone] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const navigate = useNavigate();
  const { permissions, setPermissions } = useContext(AppContext);
  const {
    hasEnforcementPermission,
    hasRateSettingsPermission,
    hasPromoCodePermission,
    hasParkingPassesPermission,
    hasRefundPortalPermission,
    hasUserPermission,
    hasUserRolePermission,
    hasTenantValidationPermission,
    promoCodePermissions,
    hasRealTimeDashboardPermission,
    hasSettledDashboardPermission,
    hasLocationsPermissions,
    token: jwtToken,
    superuser,
  } = permissions || {};

  const parkingPassesEnabled = getFeatureFlag('PARKING_PASSES_ENABLED');

  const canAddLocation = useMemo(
    () => permissions
      ?.featureLevelLocations
      ?.default
      ?.[shortFeatureNames.LOCATION]
      ?.[shortAccess[access.CAN_ADD]] ?? false,
    [permissions],
  );

  const hasBulkGenerationPermission = useMemo(
    () => permissions
      ?.featureLevelLocations
      ?.default
      ?.[shortFeatureNames.BULK_GENERATION]
      ?.[shortAccess[access.CAN_VIEW]] ?? false,
    [permissions],
  );

  const hasConnectAccountsPermission = useMemo(
    () => superuser || permissions?.defaultRole === ROLE_IDS.ACCOUNTANT,
    [permissions, superuser],
  );

  const canAddBulkPromoCodes = useMemo(
    () => permissions
      ?.featureLevelLocations
      ?.default
      ?.[shortFeatureNames.BULK_GENERATION]
      ?.[shortAccess[access.CAN_ADD]] ?? false,
    [permissions],
  );

  useEffect(() => {
    if (permissions?.user_id) {
      setBrowserContextProperty('user_id', permissions.user_id);
    } else {
      removeBrowserContextProperty('user_id');
    }

    if (permissions?.defaultRole) {
      setBrowserContextProperty('default_role', permissions.defaultRole);
    } else {
      removeBrowserContextProperty('default_role');
    }

    if (typeof permissions?.superuser === 'boolean') {
      setBrowserContextProperty('superuser', permissions.superuser);
    } else {
      removeBrowserContextProperty('superuser');
    }
  }, [permissions?.defaultRole, permissions?.superuser, permissions?.user_id]);

  const logoutFunction = useCallback(async () => {
    await auth.signOut();
    await deviceSignOut();
    localStorage.removeItem('lpa');
    setUserEmail(null);
    setAuthEnabled(false);
    navigate('/login');
  }, [navigate, setUserEmail, setAuthEnabled]);

  const validateLogin = useCallback(() => {
    auth.onAuthStateChanged(async (user) => {
      setFirebaseDone(true);
      const authPaths = ['/login'];
      if (authPaths.includes(window.location.pathname)) {
        return;
      }
      if (user) {
        setAuthEnabled(true);
        setUserEmail(user.email);
        document.body.classList.remove('login');
      } else {
        setUserEmail(null);
        setAuthEnabled(false);
        setPermissions(null);
        console.log('we aint got no user');
        logoutFunction();
        console.log(`Firebase did not pass a user object. Logging current user out.`);
      }
    });
  }, [setAuthEnabled, setUserEmail, setPermissions, logoutFunction]);

  const getMyPermissionsData = useCallback(async () => {
    const { data, success } = await loadPermissions({ userEmail });

    if (success && data?.userData) {
      const permissions = data?.userData;
      setAuthEnabled(true);
      setPermissions(permissions);
      setPermissionsLoaded(true);
    }
  }, [userEmail, setAuthEnabled, setPermissions, setPermissionsLoaded]);


  useEffect(() => {
    validateLogin();
  }, [validateLogin]);

  useEffect(() => {
    getMyPermissionsData();
  }, [userEmail, getMyPermissionsData]);

  const fallbackSpinner = () => (
    <Backdrop open>
      <CircularProgress color="primary" />
    </Backdrop>
  );

  if (!firebaseDone) {
    return fallbackSpinner();
  }

  const IS_PROD = window.location.hostname === 'lightningpayadmin.propark.com' ? true : false;

  // Public routes that render outside the authenticated layout
  if (window.location.pathname === '/onboarding-complete') {
    window.location.replace('/onboarding-complete.html');
    return fallbackSpinner();
  }

  return (
    <div className="App">
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <Layout userEmail={userEmail} logoutFunction={logoutFunction} authEnabled={authEnabled}>
            <Suspense fallback={fallbackSpinner()}>
              <Routes>
                  <Route
                    path="/"
                    element={
                      hasRealTimeDashboardPermission ? (
                        <Dashboard isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} authEnabled={authEnabled} />
                      ) : authEnabled && hasSettledDashboardPermission ? (
                        <Dashboard isLoggedIn={!!(firebaseDone && jwtToken)} settled />
                      ) : (
                        <AutoRedirect permissionsLoaded={permissionsLoaded} />
                      )
                    }
                  />
                  <Route
                    path="/rates"
                    element={(<>
                      {authEnabled && hasRateSettingsPermission ? (
                        <RateChanges isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/promo-codes"
                    element={(<>
                      {authEnabled && hasPromoCodePermission ? (
                        <PromoCodes isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/promo-codes/new"
                    element={(<>
                      {authEnabled && hasPromoCodePermission && !!promoCodePermissions?.canAdd ? (
                        <PromoCodeForm isLoggedIn={!!(firebaseDone && jwtToken)} />
                      ) : (
                        <NoPermissionsDashboard isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} authEnabled={authEnabled} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/promo-codes/edit/:promoCodeId"
                    element={(<>
                      {authEnabled && hasPromoCodePermission && !!promoCodePermissions?.canEdit ? (
                        <PromoCodeForm isLoggedIn={!!(firebaseDone && jwtToken)} />
                      ) : (
                        <NoPermissionsDashboard isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} authEnabled={authEnabled} />
                      )}
                    </>
                    )}
                  />
                  {parkingPassesEnabled ? (
                    <>
                      <Route
                        path="/passes"
                        element={(<>
                          {authEnabled && hasParkingPassesPermission ? (
                            <ParkingPasses />
                          ) : (
                            <NoPermissionsDashboard isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} authEnabled={authEnabled} />
                          )}
                        </>
                        )}
                      />
                      <Route
                        path="/passes/new-pass-type"
                        element={(<>
                          {authEnabled && hasParkingPassesPermission ? (
                            <ParkingPasses />
                          ) : (
                            <NoPermissionsDashboard isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} authEnabled={authEnabled} />
                          )}
                        </>
                        )}
                      />
                      <Route
                        path="/passes/enroll"
                        element={(<>
                          {authEnabled && hasParkingPassesPermission ? (
                            <ParkingPasses />
                          ) : (
                            <NoPermissionsDashboard isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} authEnabled={authEnabled} />
                          )}
                        </>
                        )}
                      />
                      <Route
                        path="/passes/pass-types/:passTypeId/created"
                        element={(<>
                          {authEnabled && hasParkingPassesPermission ? (
                            <ParkingPasses />
                          ) : (
                            <NoPermissionsDashboard isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} authEnabled={authEnabled} />
                          )}
                        </>
                        )}
                      />
                      <Route
                        path="/passes/pass-types/:passTypeId/edit"
                        element={(<>
                          {authEnabled && hasParkingPassesPermission ? (
                            <ParkingPasses />
                          ) : (
                            <NoPermissionsDashboard isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} authEnabled={authEnabled} />
                          )}
                        </>
                        )}
                      />
                      <Route
                        path="/passes/groups/:groupId"
                        element={(<>
                          {authEnabled && hasParkingPassesPermission ? (
                            <ParkingPasses />
                          ) : (
                            <NoPermissionsDashboard isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} authEnabled={authEnabled} />
                          )}
                        </>
                        )}
                      />
                      <Route
                        path="/passes/groups/:groupId/add-members"
                        element={(<>
                          {authEnabled && hasParkingPassesPermission ? (
                            <ParkingPasses />
                          ) : (
                            <NoPermissionsDashboard isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} authEnabled={authEnabled} />
                          )}
                        </>
                        )}
                      />
                      <Route
                        path="/passes/import"
                        element={(<>
                          {authEnabled && hasParkingPassesPermission ? (
                            <ParkingPasses />
                          ) : (
                            <NoPermissionsDashboard isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} authEnabled={authEnabled} />
                          )}
                        </>
                        )}
                      />
                      <Route
                        path="/passes/:tab"
                        element={(<>
                          {authEnabled && hasParkingPassesPermission ? (
                            <ParkingPasses />
                          ) : (
                            <NoPermissionsDashboard isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} authEnabled={authEnabled} />
                          )}
                        </>
                        )}
                      />
                    </>
                  ) : null}
                  <Route
                    path="/list"
                    element={(<>
                      {authEnabled && hasEnforcementPermission ? (
                        <ListV2 isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} />
                      ) : (
                        <NoPermissionsDashboard isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} authEnabled={authEnabled} />
                      )}
                    </>
                    )}
                  />
                  {/* <Route
                    path="/list"
                    element={(
                      <>
                        {authEnabled && hasEnforcementPermission ? (
                          <ListV2 isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} />
                        ) : (
                          <NoPermissionsDashboard isLoggedIn={!!(firebaseDone && jwtToken)} userEmail={userEmail} authEnabled={authEnabled} />
                        )}
                      </>
                    )}
                  /> */}
                  <Route
                    path="/transactions/:transactionId?"
                    element={(<>
                      {authEnabled && hasRefundPortalPermission ? (
                        <Transactions isLoggedIn={!!(firebaseDone && jwtToken)} />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/rate-config"
                    element={(<>
                      {authEnabled && hasRateSettingsPermission ? (
                        <RateConfig isLoggedIn={!!(firebaseDone && jwtToken)} />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/role-config/*"
                    element={(<>
                      {authEnabled && hasUserRolePermission ? (
                        <RoleConfig isLoggedIn={!!(firebaseDone && jwtToken)} />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/user-config/*"
                    element={(<>
                      {authEnabled && hasUserPermission ? (
                        <UserConfig isLoggedIn={!!(firebaseDone && jwtToken)} />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/settled-dashboard"
                    element={(<>
                      {authEnabled && hasSettledDashboardPermission ? (
                        <Dashboard isLoggedIn={!!(firebaseDone && jwtToken)} settled />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/rate-selection"
                    element={(<>
                      {authEnabled && hasRateSettingsPermission ? (
                        <RateCalendar />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/locations"
                    element={(<>
                      {authEnabled && (hasLocationsPermissions || canAddLocation) ? (
                        <ListLocations />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/add-location"
                    element={(<>
                      {authEnabled && (hasLocationsPermissions || canAddLocation) ? (
                        <AddLocationCard />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/add-location/success"
                    element={(<>
                      {authEnabled && (hasLocationsPermissions || canAddLocation) ? (
                        <LocationAdded />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/edit-location/:locationId"
                    element={(<>
                      {authEnabled && hasLocationsPermissions ? (
                        <EditLocation />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/mailing"
                    element={(<>
                      {authEnabled && superuser ? (
                        <Mailing isLoggedIn={!!(firebaseDone && jwtToken)} />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/login"
                    element={
                      <Login
                        isLoggedIn={!!(firebaseDone && jwtToken)}
                        firebaseDone={firebaseDone}
                        handleLoginSuccess={(email) => setUserEmail(email)}
                        validateLogin={validateLogin}
                        setLoginError={(error) => setLoginError(error)}
                        loginError={loginError}
                      />
                    }
                  />
                  <Route
                    path="/database-copy"
                    element={(<>
                      {authEnabled && superuser && !IS_PROD ? (
                        <DatabaseCopy isLoggedIn={!!(firebaseDone && jwtToken)} />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/enforcement-lite"
                    element={(<>
                      {authEnabled && superuser ? (
                        <EnforcementLite isLoggedIn={!!(firebaseDone && jwtToken)} />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/enforcement-lite/add-location"
                    element={(<>
                      {authEnabled && superuser ? (
                        <AddEnforcementLiteLocation isLoggedIn={!!(firebaseDone && jwtToken)} />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/enforcement-lite/edit-location/:locationId"
                    element={(<>
                      {authEnabled && superuser ? (
                        <EditEnforcementLiteLocation isLoggedIn={!!(firebaseDone && jwtToken)} />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/enforcement-lite/edit-user/:userId"
                    element={(<>
                      {authEnabled && superuser ? (
                        <EditEnforcementLiteUser isLoggedIn={!!(firebaseDone && jwtToken)} />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/enforcement-lite/add-user"
                    element={(<>
                      {authEnabled && superuser ? (
                        <AddEnforcementLiteUser isLoggedIn={!!(firebaseDone && jwtToken)} />
                      ) : (
                        <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                      )}
                    </>
                    )}
                  />
                  <Route
                    path="/tenant-management"
                    element={(
                      <>
                        {authEnabled && (superuser || hasBulkGenerationPermission) ? (
                          <TenantManagement isLoggedIn={!!(firebaseDone && jwtToken)} />
                        ) : (
                          <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                        )}
                      </>
                    )}
                  />
                  <Route
                    path="/tenant-management/tenant/:tenantId"
                    element={(
                      <>
                        {authEnabled && (superuser || hasBulkGenerationPermission) ? (
                          <ManageTenant isLoggedIn={!!(firebaseDone && jwtToken)} />
                        ) : (
                          <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                        )}
                      </>
                    )}
                  />
                  <Route
                    path="/tenant-validations"
                    element={(
                      <>
                        {authEnabled && hasTenantValidationPermission ? (
                          <TenantValidations isLoggedIn={!!(firebaseDone && jwtToken)} />
                        ) : (
                          <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                        )}
                      </>
                    )}
                  />
                  <Route
                    path="/connect-accounts"
                    element={(
                      <>
                        {authEnabled && hasConnectAccountsPermission ? (
                          <ConnectAccounts isLoggedIn={!!(firebaseDone && jwtToken)} />
                        ) : (
                          <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                        )}
                      </>
                    )}
                  />
                  <Route
                    path="/promo-codes/bulk/new"
                    element={
                      <>
                        {authEnabled && canAddBulkPromoCodes ? (
                          <PromoCodeForm isLoggedIn={!!(firebaseDone && jwtToken)} is_bulk={true} />
                        ) : (
                          <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                        )}
                      </>
                    }
                  />
                  <Route
                    path="/promo-codes/bulk/edit/:promoCodeId"
                    element={
                      <>
                        {authEnabled && hasPromoCodePermission && !!promoCodePermissions?.canEdit ? (
                          <PromoCodeForm isLoggedIn={!!(firebaseDone && jwtToken)} is_bulk={true} />
                        ) : (
                          <NoPermissionsDashboard userEmail={userEmail} authEnabled={authEnabled} isLoggedIn={!!(firebaseDone && jwtToken)} />
                        )}
                      </>
                    }
                  />
              </Routes>
            </Suspense>
          </Layout>
        </QueryClientProvider>
      </ErrorBoundary>
    </div>
  );
}

class ErrorBoundary extends PureComponent {
  constructor(props) {
    super(props);
    this.state = { errorMessage: '' };
  }

  static getDerivedStateFromError(error) {
    return { errorMessage: error };
  }

  componentDidCatch(error) {
    console.error('Error caught by boundary:', error?.message || error?.toString() || 'Unknown error');
  }

  render() {
    if (this.state.errorMessage) {
      return (
        <Grid container justifyContent="center">
          <Grid size={{ xs: 12, sm: 6 }}>
            <Paper className="error-boundary">
              <h3 className="error-boundary-header">Hmm. It looks like something went wrong.</h3>
              <ReportProblemTwoToneIcon className="error-boundary-icon" color="error" />
              <div className="error-boundary-footer">
                <p className="error-boundary-footer-text">Please reload the page and try again.</p>
              </div>
            </Paper>
          </Grid>
        </Grid>
      );
    }
    return this.props.children;
  }
}

export default App;
